import React from 'react';
import { Link, Badge, Tooltip } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Task } from '../api/taskApi';

// Dynamic pattern that matches any task-like ID (more flexible)
export const TASK_MENTION_PATTERN = /(?:#([A-Z]{2,}-[A-Z0-9-]+)|@task:([a-zA-Z0-9]+)|([A-Z]{2,}-[A-Z0-9-]+))/g;

export interface TaskMention {
  text: string;
  taskId: string;
  type: 'taskId' | 'mongoId' | 'projectNumber';
  start: number;
  end: number;
}

/**
 * Parse text to find task mentions
 */
export const parseTaskMentions = (text: string): TaskMention[] => {
  const mentions: TaskMention[] = [];
  let match;
  
  // Reset regex
  TASK_MENTION_PATTERN.lastIndex = 0;
  
  while ((match = TASK_MENTION_PATTERN.exec(text)) !== null) {
    const fullMatch = match[0];
    const hashTaskMatch = match[1]; // #PP-123456 or #PP-ABC-1234
    const mongoIdMatch = match[2]; // @task:id
    const plainTaskMatch = match[3]; // PP-123456 or PP-ABC-1234
    
    let taskId: string;
    let type: 'taskId' | 'mongoId' | 'projectNumber';
    
    if (hashTaskMatch) {
      taskId = hashTaskMatch;
      type = 'taskId';
    } else if (mongoIdMatch) {
      taskId = mongoIdMatch;
      type = 'mongoId';
    } else if (plainTaskMatch) {
      taskId = plainTaskMatch;
      type = 'projectNumber';
    } else {
      continue;
    }
    
    mentions.push({
      text: fullMatch,
      taskId,
      type,
      start: match.index,
      end: match.index + fullMatch.length,
    });
  }
  
  return mentions;
};

/**
 * Find a task by any identifier - works with dynamic formats
 */
export const findTaskByIdentifier = (
  tasks: Task[],
  identifier: string,
  type: 'taskId' | 'mongoId' | 'projectNumber'
): Task | undefined => {
  return tasks.find(task => {
    switch (type) {
      case 'taskId':
      case 'projectNumber':
        // For any task-like ID, check against task.taskId
        return task.taskId === identifier;
      case 'mongoId':
        return task._id === identifier;
      default:
        return false;
    }
  });
};

interface TaskLinkProps {
  mention: TaskMention;
  task?: Task;
  onNavigate?: (taskId: string) => void;
}

/**
 * Clickable task link component
 */
export const TaskLink: React.FC<TaskLinkProps> = ({ mention, task, onNavigate }) => {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (task) {
      if (onNavigate) {
        onNavigate(task._id);
      } else {
        navigate(`/dashboard/task/${task._id}`);
      }
    }
  };
  
  if (!task) {
    // Task not found - show as disabled
    return (
      <Tooltip label="Task not found">
        <Badge
          colorScheme="gray"
          variant="outline"
          fontSize="xs"
          cursor="not-allowed"
          opacity={0.6}
        >
          {mention.text}
        </Badge>
      </Tooltip>
    );
  }
  
  return (
    <Tooltip label={`${task.taskTitle} (${task.status})`}>
      <Link
        as="button"
        onClick={handleClick}
        color="blue.500"
        textDecoration="underline"
        fontWeight="medium"
        fontSize="sm"
        _hover={{ color: 'blue.600', textDecoration: 'none' }}
        cursor="pointer"
      >
        <Badge
          colorScheme="blue"
          variant="subtle"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="md"
          _hover={{ bg: 'blue.100' }}
        >
          {mention.text}
        </Badge>
      </Link>
    </Tooltip>
  );
};

interface TaskMentionRendererProps {
  text: string;
  tasks: Task[];
  onNavigate?: (taskId: string) => void;
}

/**
 * Component that renders text with clickable task mentions
 */
export const TaskMentionRenderer: React.FC<TaskMentionRendererProps> = ({ 
  text, 
  tasks, 
  onNavigate 
}) => {
  const mentions = parseTaskMentions(text);
  
  if (mentions.length === 0) {
    return <span>{text}</span>;
  }
  
  const elements: React.ReactNode[] = [];
  let lastIndex = 0;
  
  mentions.forEach((mention, index) => {
    // Add text before the mention
    if (mention.start > lastIndex) {
      elements.push(text.slice(lastIndex, mention.start));
    }
    
    // Find the actual task
    const task = findTaskByIdentifier(tasks, mention.taskId, mention.type);
    
    // Add the task link
    elements.push(
      <TaskLink
        key={`mention-${index}`}
        mention={mention}
        task={task}
        onNavigate={onNavigate}
      />
    );
    
    lastIndex = mention.end;
  });
  
  // Add remaining text
  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }
  
  return <>{elements}</>;
};

/**
 * Enhanced hook for task auto-completion with smart filtering
 */
export const useTaskAutoComplete = (tasks: Task[]) => {
  const getSuggestions = (query: string): Task[] => {
    if (query.length < 1) return [];
    
    const lowerQuery = query.toLowerCase();
    
    // If query starts with 'p', show all tasks that start with PP-, P-, etc.
    if (lowerQuery.startsWith('p')) {
      return tasks
        .filter(task => 
          task.taskId.toLowerCase().startsWith('p') ||
          task.taskTitle.toLowerCase().includes(lowerQuery.substring(1)) ||
          (task.project && typeof task.project === 'object' && 
           task.project.projectName.toLowerCase().includes(lowerQuery.substring(1)))
        )
        .slice(0, 10);
    }
    
    // General search
    return tasks
      .filter(task => 
        task.taskTitle.toLowerCase().includes(lowerQuery) ||
        task.taskId.toLowerCase().includes(lowerQuery) ||
        (task.project && typeof task.project === 'object' && 
         task.project.projectName.toLowerCase().includes(lowerQuery))
      )
      .slice(0, 10);
  };
  
  const formatSuggestion = (task: Task): string => {
    return `#${task.taskId}`;
  };
  
  return { getSuggestions, formatSuggestion };
};
